import { Application, Assets, Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js'
import type { SavasDurumu, SavasBirimi, Taraf } from '@kurultay/savas'
import { birimYani, birimKonumu, birimOlcegi, TABAN_Y } from './yerlesim.js'
import { fonSec } from './fonlar.js'
import type { Yan } from './yerlesim.js'

const RENK = {
  sol: 0xc9a227,
  sag: 0xa33b2a,
  gokUst: 0x2b2a3d,
  gokAlt: 0x6b5a46,
  dagUzak: 0x3a3548,
  dagYakin: 0x2e2a38,
  bozkirUzak: 0x453a26,
  bozkirYakin: 0x2f2719,
  can: 0x6ba368,
  canBos: 0x1a140d,
  metin: 0xe8dcc4,
  fiziksel: 0xe8dcc4,
  ruh: 0x8db4d8,
  kritik: 0xffcf5c,
  isabetsiz: 0x6b6459,
} as const

interface BirimGorseli {
  kok: Container
  govde: Graphics
  canBari: Graphics
  ad: Text
  boy: number
}

const yaziStili = (boyut: number, renk: number) =>
  new TextStyle({ fontFamily: 'system-ui, sans-serif', fontSize: boyut, fill: renk })

export class SavasSahnesi {
  readonly #kok = new Container()
  readonly #fon = new Container()
  readonly #karartma = new Graphics()
  readonly #birimKatmani = new Container()
  readonly #efektKatmani = new Container()
  readonly #gorseller = new Map<string, BirimGorseli>()
  readonly #yerlesim = new Map<string, { indeks: number; sayi: number }>()
  #oyuncuTarafi: Taraf = 'a'
  #fonSprite: Sprite | null = null
  #fonKodu = 'otuken_ormani'

  readonly #boyutDinleyici = () => this.yenidenKonumla()

  constructor(private readonly app: Application) {
    this.app.stage.addChild(this.#fon)
    this.app.stage.addChild(this.#karartma)
    this.#kok.addChild(this.#birimKatmani, this.#efektKatmani)
    this.app.stage.addChild(this.#kok)
    // Pencere boyutu değişince fon ve sahne kökü yeniden hesaplanmalı;
    // aksi halde tuval büyür ama içerik eski yerinde kalır.
    this.app.renderer.on('resize', this.#boyutDinleyici)
    this.yenidenKonumla()
  }

  yenidenKonumla(): void {
    const { width, height } = this.app.renderer
    this.#kok.x = width / 2
    // Birimler sahnenin alt üçte birinde dursun; fon üstte nefes alsın.
    this.#kok.y = height * 0.56
    if (this.#fonSprite) this.#fonuKapla(width, height)
    else this.#fonuCiz(width, height)
    this.#karartmayiCiz(width, height)
  }

  /**
   * Üretilmiş fon görselini yükler. Yoksa çizilmiş fona düşülür.
   * Yükleme asenkron olduğu için çizilmiş fon önce gösterilir; görsel
   * hazır olunca üstüne geçer — böylece sahne hiçbir an boş kalmaz.
   */
  async fonuYukle(kod: string): Promise<void> {
    this.#fonKodu = kod
    const tanim = fonSec(kod)
    if (!tanim.gorsel) return

    try {
      const doku = await Assets.load(tanim.gorsel)
      // Yükleme sırasında sahne yok edilmiş olabilir
      if (this.#fon.destroyed) return
      this.#fon.removeChildren()
      const sprite = new Sprite(doku)
      sprite.anchor.set(0.5)
      this.#fon.addChild(sprite)
      this.#fonSprite = sprite
      this.#fonuKapla(this.app.renderer.width, this.app.renderer.height)
    } catch {
      // Görsel yüklenemezse çizilmiş fon zaten ekranda, sessizce devam
    }
  }

  /**
   * Birimlerin arkasına yumuşak bir karartma. İki işi var:
   * altın birimlerin altın bozkırda erimesini engeller ve alt kenarı
   * arayüz barına yumuşak bağlar.
   */
  #karartmayiCiz(genislik: number, yukseklik: number): void {
    this.#karartma.clear()
    const bant = 26
    for (let i = 0; i < bant; i++) {
      const t = i / (bant - 1)
      // Üstte yok, aşağı indikçe artan karartma
      const alfa = 0.42 * t * t
      this.#karartma
        .rect(0, yukseklik * 0.34 + ((yukseklik * 0.66) / bant) * i,
              genislik, (yukseklik * 0.66) / bant + 1)
        .fill({ color: 0x14110d, alpha: alfa })
    }
    // Kenarlardan hafif vinyet
    this.#karartma
      .rect(0, 0, genislik * 0.1, yukseklik).fill({ color: 0x14110d, alpha: 0.22 })
      .rect(genislik * 0.9, 0, genislik * 0.1, yukseklik).fill({ color: 0x14110d, alpha: 0.22 })
  }

  /** Sprite'ı tuvali tamamen kaplayacak şekilde ölçekler (cover). */
  #fonuKapla(genislik: number, yukseklik: number): void {
    const s = this.#fonSprite
    if (!s) return
    const olcek = Math.max(genislik / s.texture.width, yukseklik / s.texture.height)
    s.scale.set(olcek)
    s.x = genislik / 2
    // Ufuk çizgisi birimlerin arkasına denk gelsin diye hafifçe yukarı kaydırılır
    s.y = yukseklik * 0.46
  }

  /**
   * Bozkır fonu: gök, uzak dağlar, ova katmanları.
   * Boyalı sanat yerine katmanlı siluet — Faz 2'de görsel hedef "okunaklı",
   * "güzel" değil. Gerçek arka plan sanatı sonra gelecek.
   */
  #fonuCiz(genislik: number, yukseklik: number): void {
    this.#fon.removeChildren()
    const ufuk = yukseklik * 0.34

    // Gök: birkaç bantla kaba gradyan
    const bant = 14
    for (let i = 0; i < bant; i++) {
      const t = i / (bant - 1)
      const r = Math.round(0x2b + (0x6b - 0x2b) * t)
      const g = Math.round(0x2a + (0x5a - 0x2a) * t)
      const b = Math.round(0x3d + (0x46 - 0x3d) * t)
      this.#fon.addChild(
        new Graphics()
          .rect(0, (ufuk / bant) * i, genislik, ufuk / bant + 1)
          .fill({ color: (r << 16) | (g << 8) | b }),
      )
    }

    // Uzak dağ silueti
    const dagUzak = new Graphics().moveTo(0, ufuk)
    for (let x = 0; x <= genislik; x += genislik / 10) {
      dagUzak.lineTo(x, ufuk - 50 - Math.sin(x / 190) * 42 - Math.cos(x / 95) * 16)
    }
    dagUzak.lineTo(genislik, ufuk).lineTo(0, ufuk).closePath()
      .fill({ color: RENK.dagUzak })
    this.#fon.addChild(dagUzak)

    // Yakın tepeler
    const dagYakin = new Graphics().moveTo(0, ufuk)
    for (let x = 0; x <= genislik; x += genislik / 7) {
      dagYakin.lineTo(x, ufuk - 22 - Math.sin(x / 130 + 2) * 26)
    }
    dagYakin.lineTo(genislik, ufuk).lineTo(0, ufuk).closePath()
      .fill({ color: RENK.dagYakin })
    this.#fon.addChild(dagYakin)

    // Ova: uzak ve yakın iki katman
    this.#fon.addChild(
      new Graphics().rect(0, ufuk, genislik, yukseklik - ufuk).fill({ color: RENK.bozkirUzak }),
    )
    this.#fon.addChild(
      new Graphics()
        .moveTo(0, ufuk + (yukseklik - ufuk) * 0.42)
        .bezierCurveTo(genislik * 0.3, ufuk + (yukseklik - ufuk) * 0.3,
                       genislik * 0.7, ufuk + (yukseklik - ufuk) * 0.52,
                       genislik, ufuk + (yukseklik - ufuk) * 0.36)
        .lineTo(genislik, yukseklik).lineTo(0, yukseklik).closePath()
        .fill({ color: RENK.bozkirYakin }),
    )
  }

  birimleriKur(durum: SavasDurumu, oyuncuTarafi: Taraf): void {
    this.#oyuncuTarafi = oyuncuTarafi
    this.#birimKatmani.removeChildren()
    this.#gorseller.clear()
    this.#yerlesim.clear()

    // Her yan+saf grubunun kaç birim taşıdığını önce say
    const anahtar = (b: SavasBirimi) => `${birimYani(oyuncuTarafi, b.taraf)}-${b.saf}`
    const doluluk = new Map<string, number>()
    for (const b of durum.birimler) {
      doluluk.set(anahtar(b), (doluluk.get(anahtar(b)) ?? 0) + 1)
    }

    const sayac = new Map<string, number>()
    for (const b of durum.birimler) {
      const k = anahtar(b)
      const indeks = sayac.get(k) ?? 0
      sayac.set(k, indeks + 1)
      const sayi = doluluk.get(k) ?? 1
      this.#yerlesim.set(b.id, { indeks, sayi })
      this.#birimEkle(b, birimYani(oyuncuTarafi, b.taraf), indeks, sayi)
    }
    this.#derinlikSirala()
  }

  #birimEkle(b: SavasBirimi, yan: Yan, indeks: number, sayi: number): void {
    const kahraman = b.tur === 'kahraman'
    const { x, y } = birimKonumu(yan, b.saf, indeks, sayi)

    const kok = new Container()
    kok.x = x
    kok.y = y
    kok.scale.set(birimOlcegi(y))

    const boy = kahraman ? 96 : 72
    const en = kahraman ? 32 : 25
    const bakis = yan === 'sol' ? 1 : -1

    // Kaba insan silueti: gövde + baş + bakış yönünü gösteren çentik
    const govde = new Graphics()
      .roundRect(-en / 2, -boy, en, boy * 0.72, 4)
      .fill({ color: RENK[yan] })
      .circle(0, -boy - 9, en * 0.44)
      .fill({ color: RENK[yan] })
      .moveTo(bakis * en * 0.5, -boy + 8)
      .lineTo(bakis * (en * 0.5 + 16), -boy + 20)
      .lineTo(bakis * en * 0.5, -boy + 26)
      .closePath()
      .fill({ color: RENK[yan], alpha: 0.75 })
    govde.stroke({ color: kahraman ? 0xffffff : 0x000000, width: kahraman ? 2 : 1 })

    // Yere gölge
    const golge = new Graphics().ellipse(0, 0, en * 0.9, 7).fill({ color: 0x000000, alpha: 0.3 })

    const ad = new Text({
      text: kahraman ? `★ ${b.tur === 'kahraman' ? b.meslek : ''}` : `${b.ceriTuru} ×${b.adet}`,
      style: yaziStili(kahraman ? 13 : 11, RENK.metin),
    })
    ad.anchor.set(0.5)
    ad.y = 26

    const canBari = new Graphics()
    kok.addChild(golge, govde, canBari, ad)
    this.#birimKatmani.addChild(kok)

    const gorsel: BirimGorseli = { kok, govde, canBari, ad, boy }
    this.#gorseller.set(b.id, gorsel)
    this.#canBariCiz(gorsel, b.can, b.azamiCan)
  }

  #canBariCiz(g: BirimGorseli, can: number, azami: number): void {
    const oran = azami > 0 ? Math.max(0, can / azami) : 0
    g.canBari.clear()
      .rect(-27, -g.boy - 26, 54, 6).fill({ color: RENK.canBos })
      .rect(-27, -g.boy - 26, 54 * oran, 6).fill({ color: RENK.can })
  }

  /** Aşağıdaki birim önde çizilir. */
  #derinlikSirala(): void {
    this.#birimKatmani.children.sort((a, b) => a.y - b.y)
  }

  birimGuncelle(id: string, can: number, azamiCan: number): void {
    const g = this.#gorseller.get(id)
    if (g) this.#canBariCiz(g, can, azamiCan)
  }

  safGuncelle(id: string, taraf: Taraf, saf: 'on' | 'arka'): void {
    const g = this.#gorseller.get(id)
    const yer = this.#yerlesim.get(id)
    if (!g || !yer) return
    const { x, y } = birimKonumu(birimYani(this.#oyuncuTarafi, taraf), saf, yer.indeks, yer.sayi)
    g.kok.x = x
    g.kok.y = y
    g.kok.scale.set(birimOlcegi(y))
    this.#derinlikSirala()
  }

  hasarGoster(
    id: string, hasar: number, hasarTuru: 'fiziksel' | 'ruh',
    kritik: boolean, isabetEtti: boolean,
  ): void {
    const g = this.#gorseller.get(id)
    if (!g) return
    const renk = !isabetEtti ? RENK.isabetsiz : kritik ? RENK.kritik : RENK[hasarTuru]
    const metin = new Text({
      text: isabetEtti ? `${kritik ? '✦' : ''}${hasar}` : 'ıskaladı',
      style: yaziStili(isabetEtti && kritik ? 24 : 17, renk),
    })
    metin.anchor.set(0.5)
    metin.x = g.kok.x
    metin.y = g.kok.y - g.boy - 34
    this.#efektKatmani.addChild(metin)

    let gecen = 0
    const tik = (t: { deltaMS: number }) => {
      gecen += t.deltaMS
      metin.y -= t.deltaMS * 0.04
      metin.alpha = Math.max(0, 1 - gecen / 950)
      if (gecen > 950) {
        this.app.ticker.remove(tik)
        metin.destroy()
      }
    }
    this.app.ticker.add(tik)
  }

  birimOldur(id: string): void {
    const g = this.#gorseller.get(id)
    if (!g) return
    g.kok.alpha = 0.2
    g.govde.tint = 0x555555
  }

  yokEt(): void {
    this.#fonSprite = null
    this.#karartma.destroy()
    this.app.renderer.off('resize', this.#boyutDinleyici)
    this.#fon.destroy({ children: true })
    this.#kok.destroy({ children: true })
  }
}
